/**
 * QuickCommsView + SourceModePanel + LibraryModePanel — the two-mode
 * ad-hoc / from-library pack generator.
 *
 * Extracted from App.js during P1 Phase-3 refactor. Zero behavioural
 * changes.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { API, extractErrorDetail, ACCEPTED_FILE_EXT } from "../lib/beaconUtils";
import { AppliedContextStrip } from "./ContextEngineView.jsx";
import { PackWorkspace } from "./PackWorkspace.jsx";

export function QuickCommsView({
  form,
  appliedProfile, profileIsDirty, appliedAt,
  topics, profileValid,
  generating, setGenerating, setGenerationError, generationError,
  setCurrentPack, currentPack,
  jumpTopic, clearJumpTopic,
  rebuildRequest, clearRebuildRequest,
  onEditContext,
}) {
  const [mode, setMode] = useState("source");

  // Arriving from the Annual Plan with a specific week's topic — switch
  // straight into library mode so the handover is visible.
  useEffect(() => {
    if (jumpTopic) setMode("library");
  }, [jumpTopic]);
  // Incrementing this key force-remounts the active panel, wiping its
  // internal state (uploaded files, instructions, library selections)
  // without lifting all that state into this parent.
  const [panelKey, setPanelKey] = useState(0);
  const generateProfile = appliedProfile || form;
  const generationLocked = profileIsDirty || !appliedProfile;

  const clearWorkspace = () => {
    setCurrentPack(null);
    setGenerationError(null);
    setPanelKey((k) => k + 1);
    // Scroll the user back up to the generate card so the reset is visible.
    const card = document.querySelector('[data-testid="generate-card"]');
    if (card) card.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <AppliedContextStrip
        appliedProfile={appliedProfile}
        appliedAt={appliedAt}
        onEditContext={onEditContext}
      />
      <div className="beacon-quick-stack">
        <section className="beacon-card" data-testid="generate-card">
          <div className="beacon-card-eyebrow">QUICK COMMS · AD-HOC PACK</div>
          <h2 className="beacon-card-title">Build a comms pack for this thing.</h2>
          <p className="beacon-card-lede">
            Hand Beacon a procedure, incident notice, investigation, regulatory
            update — or pick a topic from the library. Either way you get the
            four-format pack: poster, toolbox handout, email, intranet article.
          </p>

          {generationLocked && (
            <div className="beacon-alert beacon-alert-soft"
                 data-testid="apply-context-prompt">
              {appliedProfile ? (
                <>
                  You have unsaved changes on the Context Engine. <button
                    type="button"
                    className="beacon-inline-link"
                    data-testid="apply-context-prompt-link"
                    onClick={onEditContext}
                  >Re-apply your context</button> before generating.
                </>
              ) : (
                <>
                  Apply your context engine first so industry-specific topics
                  show up in the dropdowns. <button
                    type="button"
                    className="beacon-inline-link"
                    data-testid="apply-context-prompt-link"
                    onClick={onEditContext}
                  >Open the Context Engine →</button>
                </>
              )}
            </div>
          )}

          {rebuildRequest ? (
            <RebuildPanel
              key={`rebuild-${rebuildRequest.packId}`}
              form={generateProfile}
              profileValid={profileValid && !generationLocked}
              generating={generating}
              setGenerating={setGenerating}
              setGenerationError={setGenerationError}
              setCurrentPack={setCurrentPack}
              rebuild={rebuildRequest}
              onDismiss={() => clearRebuildRequest?.()}
            />
          ) : (
          <>
          <div className="beacon-mode-switcher" data-testid="quick-mode-switcher">
            <button
              type="button"
              data-testid="quick-mode-source"
              className={`beacon-mode-btn${mode === "source" ? " is-active" : ""}`}
              onClick={() => setMode("source")}
            >
              <span>From a source</span>
              <span className="beacon-mode-sub">Upload + instruct Beacon</span>
            </button>
            <button
              type="button"
              data-testid="quick-mode-library"
              className={`beacon-mode-btn${mode === "library" ? " is-active" : ""}`}
              onClick={() => setMode("library")}
            >
              <span>From the library</span>
              <span className="beacon-mode-sub">Theme & topic</span>
            </button>
          </div>

          {mode === "source" ? (
            <SourceModePanel
              key={`source-${panelKey}`}
              form={generateProfile}
              profileValid={profileValid && !generationLocked}
              generating={generating}
              setGenerating={setGenerating}
              setGenerationError={setGenerationError}
              setCurrentPack={setCurrentPack}
            />
          ) : (
            <LibraryModePanel
              key={`library-${panelKey}`}
              topics={topics}
              form={generateProfile}
              profileValid={profileValid && !generationLocked}
              generating={generating}
              setGenerating={setGenerating}
              setGenerationError={setGenerationError}
              setCurrentPack={setCurrentPack}
              jumpTopic={jumpTopic}
              clearJumpTopic={clearJumpTopic}
            />
          )}
          </>
          )}

          {generationError && (
            <div className="beacon-alert" data-testid="generation-error">
              {generationError}
            </div>
          )}
        </section>
      </div>

      {(generating || currentPack) && (
        <PackWorkspace
          pack={currentPack}
          generating={generating}
          onClear={clearWorkspace}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Source mode (file upload + paste)
// ---------------------------------------------------------------------------

function SourceModePanel({
  form, profileValid, generating, setGenerating, setGenerationError,
  setCurrentPack,
}) {
  const [files, setFiles] = useState([]);
  const [instructions, setInstructions] = useState("");
  const [dragOver, setDragOver] = useState(false);

  // Instructions on their own can't generate a topic — Beacon needs source
  // material to work from. At least one file is required.
  const hasInput = files.length > 0;
  const canGenerate = profileValid && hasInput && !generating;

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles(picked);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const generate = async () => {
    setGenerating(true);
    setGenerationError(null);
    setCurrentPack(null);
    try {
      const fd = new FormData();
      fd.append("profile", JSON.stringify(form));
      if (instructions.trim()) fd.append("user_hint", instructions.trim());
      for (const f of files) fd.append("files", f);
      const r = await axios.post(`${API}/quick/from-source`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180_000,
      });
      setCurrentPack(r.data);
    } catch (err) {
      setGenerationError(await extractErrorDetail(err));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="beacon-source-panel" data-testid="source-mode-panel">
      <div className="beacon-field">
        <label className="beacon-label">
          Upload source documents
          <span className="beacon-required">PDF · DOCX · TXT · multi-file ok</span>
        </label>
        <label
          className={`beacon-file-drop${dragOver ? " is-drag-over" : ""}`}
          data-testid="file-drop"
          onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={(e) => {
            // Only clear when leaving the drop target itself (not a child).
            if (e.currentTarget === e.target) setDragOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const dropped = Array.from(e.dataTransfer.files || []).filter((f) =>
              /\.(pdf|docx|txt)$/i.test(f.name)
            );
            if (dropped.length) setFiles((prev) => [...prev, ...dropped]);
          }}
        >
          <input
            type="file"
            multiple
            accept={ACCEPTED_FILE_EXT}
            onChange={onFiles}
            data-testid="file-input"
            style={{ display: "none" }}
          />
          {files.length === 0 ? (
            <span className="beacon-file-drop-hint">
              {dragOver
                ? "Drop the files to add them"
                : "Drag & drop, or click to pick files"}
              <small>Procedure · incident notice · investigation · reg change</small>
            </span>
          ) : (
            <ul className="beacon-file-list" data-testid="file-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} data-testid={`file-item-${i}`}>
                  <span className="beacon-file-name">{f.name}</span>
                  <span className="beacon-file-size">
                    {Math.max(1, Math.round(f.size / 1024))} KB
                  </span>
                  <button
                    type="button"
                    className="beacon-file-remove"
                    data-testid={`file-remove-${i}`}
                    onClick={(e) => { e.preventDefault(); removeFile(i); }}
                  >×</button>
                </li>
              ))}
            </ul>
          )}
        </label>
      </div>

      <div className="beacon-field">
        <label className="beacon-label">
          Instructions for Beacon
          <span className="beacon-required">optional · ~2k chars</span>
        </label>
        <textarea
          className="beacon-input beacon-textarea"
          data-testid="input-instructions"
          rows={5}
          maxLength={2000}
          placeholder={"e.g. Use the attached incident investigation to create a Lessons Learned for the crew. Don't name anyone — focus on the supervisor decision point."}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
        />
        <span className="beacon-charcount">
          {instructions.length}/2000
        </span>
        <div className="beacon-lens-row" data-testid="lens-chips">
          {[
            ["lens-lessons", "Lessons Learned / Root Cause",
             "ANGLE — Lessons Learned / Root Cause: frame the content around what actually went wrong and why — systems and conditions, not blame. Draw the causal chain from the source material and land on the 2-3 controls that would have broken it."],
            ["lens-supervisor", "Supervisor Decision Point",
             "ANGLE — Supervisor Decision Point: write for the frontline leader. Focus on the moments where a supervisor's call changes the outcome — when to stop work, what to check before signing off, and what pressure to push back on."],
            ["lens-habit", "Frontline Habit & Hazard Scan",
             "ANGLE — Frontline Habit & Hazard Scan: make it about the crew's daily habits. Turn the content into concrete what-to-look-for cues and one small habit change per person — not policy talk."],
            ["lens-regulatory", "Regulatory & Duty of Care Refresh",
             "ANGLE — Regulatory & Duty of Care Refresh: frame around everyone's duty of care and good-practice expectations, referring to codes of practice by plain name only — never cite legislation, sections or regulation numbers. Practical obligations in plain language."],
          ].map(([id, label, text]) => {
            const active = instructions.includes(text);
            return (
              <button key={id} type="button"
                className={`beacon-chip beacon-lens-chip${active ? " is-active" : ""}`}
                data-testid={id}
                onClick={() => setInstructions((prev) => {
                  if (prev.includes(text)) {
                    return prev.replace(text, "").replace(/\n{3,}/g, "\n\n").trim();
                  }
                  return `${prev.trim()}\n\n${text}`.trim().slice(0, 2000);
                })}
              >{label}</button>
            );
          })}
        </div>
        <p className="beacon-hint">
          Tell Beacon what to do with the uploaded content — angle, audience,
          framing, what to emphasise or avoid. Leave blank and Beacon picks
          the default lens based on the source.
        </p>
      </div>

      <button
        type="button"
        className="beacon-cta"
        data-testid="generate-from-source"
        disabled={!canGenerate}
        onClick={generate}
      >
        {generating ? (
          <><span className="beacon-spinner" /> Building the pack…</>
        ) : (
          <>Generate comms pack from source</>
        )}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rebuild mode (regeneration brief handed over from Luma's review)
// ---------------------------------------------------------------------------

function RebuildPanel({
  form, profileValid, generating, setGenerating, setGenerationError,
  setCurrentPack, rebuild, onDismiss,
}) {
  const [packTitle, setPackTitle] = useState("");
  useEffect(() => {
    axios.get(`${API}/packs/${rebuild.packId}`)
      .then((r) => setPackTitle(r.data.title))
      .catch(() => {});
  }, [rebuild.packId]);

  const canGenerate = profileValid && !generating;

  const generate = async () => {
    setGenerating(true);
    setGenerationError(null);
    setCurrentPack(null);
    try {
      const r = await axios.post(`${API}/quick/rebuild`, {
        profile: form,
        pack_id: rebuild.packId,
        brief: rebuild.brief,
      }, { timeout: 180_000 });
      setCurrentPack(r.data);
      onDismiss?.();
    } catch (err) {
      setGenerationError(await extractErrorDetail(err));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="beacon-library-panel" data-testid="rebuild-panel">
      <div className="beacon-prompt-peek beacon-plan-topic-card"
        data-testid="rebuild-card">
        <div className="beacon-prompt-peek-eyebrow">
          REBUILD FROM LUMA&rsquo;S REVIEW
          <button type="button" className="beacon-linklike"
            data-testid="rebuild-dismiss"
            onClick={onDismiss}>
            × Discard and build something else
          </button>
        </div>
        <div className="beacon-prompt-peek-title" data-testid="rebuild-pack-title">
          {packTitle || "Reviewed pack"}
        </div>
        <div className="beacon-prompt-peek-body"
          style={{ whiteSpace: "pre-wrap" }}
          data-testid="rebuild-brief-text">
          {rebuild.brief}
        </div>
      </div>
      <p className="beacon-hint">
        Beacon regenerates the full four-format pack for this topic with
        every point in the brief applied. The original pack stays in your
        Library untouched.
      </p>
      <button
        type="button"
        className="beacon-cta"
        data-testid="generate-rebuild"
        disabled={!canGenerate}
        onClick={generate}
      >
        {generating ? (
          <><span className="beacon-spinner" /> Rebuilding the pack…</>
        ) : (
          <>Rebuild comms pack with these fixes</>
        )}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Library mode (cascading Theme → Topic dropdowns)
// ---------------------------------------------------------------------------

function LibraryModePanel({
  topics, form, profileValid, generating, setGenerating,
  setGenerationError, setCurrentPack, jumpTopic, clearJumpTopic,
}) {
  // Build {themeName: [topic, ...]} preserving calendar order.
  const themes = useMemo(() => {
    const map = new Map();
    for (const t of topics) {
      if (!map.has(t.theme_name)) map.set(t.theme_name, []);
      map.get(t.theme_name).push(t);
    }
    return Array.from(map.entries()).map(([name, items]) => ({
      name, items,
    }));
  }, [topics]);

  const [themeName, setThemeName] = useState(
    () => topics[0]?.theme_name || ""
  );
  const [topicId, setTopicId] = useState(() => topics[0]?.id || "");

  // Topic handed over from the Annual Plan. It always becomes an explicit
  // generation target (plan-topic card) — even for library topics — so the
  // scheduled month/week travels with it and the content anchors to the
  // ACTUAL delivery week, not the topic's canonical calendar slot.
  const [planTopic, setPlanTopic] = useState(null);
  useEffect(() => {
    if (!jumpTopic) return;
    setPlanTopic(jumpTopic);
  }, [jumpTopic]);

  const dismissPlanTopic = () => {
    setPlanTopic(null);
    clearJumpTopic?.();
  };

  // When the topic list arrives after first render, hydrate defaults once.
  // Functional updates keep this safe to run on every `themes` change.
  useEffect(() => {
    if (themes.length === 0) return;
    setThemeName((prev) => prev || themes[0].name);
    setTopicId((prev) => prev || themes[0].items[0]?.id || "");
  }, [themes]);

  // When theme changes, reset topic to first under the new theme.
  const onThemeChange = (next) => {
    setThemeName(next);
    const themeRow = themes.find((th) => th.name === next);
    setTopicId(themeRow?.items[0]?.id || "");
  };

  const topicsForTheme =
    themes.find((th) => th.name === themeName)?.items || [];
  const selectedTopic = topics.find((t) => t.id === topicId);

  const canGenerate = profileValid && (planTopic || topicId) && !generating;

  const generate = async () => {
    setGenerating(true);
    setGenerationError(null);
    setCurrentPack(null);
    try {
      const r = await axios.post(`${API}/quick/from-library`, {
        profile: form,
        topic_id: planTopic ? planTopic.topic_id : topicId,
        scheduled_month: planTopic?.month || undefined,
        scheduled_week: planTopic?.week || undefined,
        scheduled_theme: planTopic?.theme_name || undefined,
      }, { timeout: 180_000 });
      setCurrentPack(r.data);
    } catch (err) {
      setGenerationError(await extractErrorDetail(err));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="beacon-library-panel" data-testid="library-mode-panel">
      {planTopic ? (
        <div className="beacon-prompt-peek beacon-plan-topic-card"
          data-testid="plan-topic-card">
          <div className="beacon-prompt-peek-eyebrow">
            FROM YOUR ANNUAL PLAN
            {planTopic.month && ` · ${planTopic.month.toUpperCase()}${planTopic.week ? ` · WEEK ${planTopic.week}` : ""}`}
            <button type="button" className="beacon-linklike"
              data-testid="plan-topic-dismiss"
              onClick={dismissPlanTopic}>
              × Pick from the library instead
            </button>
          </div>
          <div className="beacon-prompt-peek-title" data-testid="plan-topic-name">
            {planTopic.topic_name}
          </div>
          {planTopic.topic_framing && (
            <div className="beacon-prompt-peek-body">
              {planTopic.topic_framing}
            </div>
          )}
          <div className="beacon-prompt-peek-body">
            Theme: {planTopic.theme_name}
          </div>
        </div>
      ) : (
        <>
      <div className="beacon-field">
        <label className="beacon-label">Theme (monthly)</label>
        <select
          className="beacon-input"
          data-testid="select-theme"
          value={themeName}
          onChange={(e) => onThemeChange(e.target.value)}
        >
          {themes.map((th) => (
            <option key={th.name} value={th.name}>{th.name}</option>
          ))}
        </select>
      </div>

      <div className="beacon-field">
        <label className="beacon-label">Topic (weekly)</label>
        <select
          className="beacon-input"
          data-testid="select-topic"
          value={topicId}
          onChange={(e) => setTopicId(e.target.value)}
        >
          {topicsForTheme.map((t) => (
            <option key={t.id} value={t.id}>
              {`${t.month} W${t.week} — ${t.topic_name}`}
            </option>
          ))}
        </select>
      </div>

      {selectedTopic && (
        <div className="beacon-prompt-peek" data-testid="topic-preview">
          <div className="beacon-prompt-peek-eyebrow">
            {selectedTopic.month.toUpperCase()} · WEEK {selectedTopic.week}
            {selectedTopic.relevant_for_industry === false && (
              <span className="beacon-not-relevant-pill">
                NOT IN YOUR INDUSTRY DEFAULT
              </span>
            )}
            {selectedTopic.relevant_for_industry === true && (
              <span className="beacon-relevant-pill">FOR YOUR INDUSTRY</span>
            )}
          </div>
          <div className="beacon-prompt-peek-title">
            {selectedTopic.topic_name}
          </div>
          <div className="beacon-prompt-peek-body">
            {selectedTopic.topic_framing}
          </div>
        </div>
      )}
        </>
      )}

      <button
        type="button"
        className="beacon-cta"
        data-testid="generate-from-library"
        disabled={!canGenerate}
        onClick={generate}
      >
        {generating ? (
          <><span className="beacon-spinner" /> Building the pack…</>
        ) : (
          <>Generate comms pack</>
        )}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pack Workspace (tabbed outputs)
// ---------------------------------------------------------------------------
